import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { io } from 'socket.io-client';

const NotificationContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification sound URL (WhatsApp-like notification)
const NOTIFICATION_SOUND = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZNJlLmAAAAAAD/+1DEAAAGAAGn9AAAIgAANP8AAAQAAC0fH4fAgCAIHD4fwQBAEAQOHw+BwIAgCBw+HwOBAEAQOHw/ggCAIAgCBw+H8EAQBAEDh8P4IAgCAIHD4fwQBAEAQBfygIAgCAIAgc+XLly5cuXLly5c//tQxAYAAADSAAAAAAAAANIAAAAASZcuXLly5cuXLly5cuXLlzEuXLly5cuXLl/KAgCAIAgCBz5cuXLly5cuXLly5cuX8oCBz5cuXLly5cuXL+UBA58uXLly5cuXLly/lAQOfLly5cuXLly5cv5QEDny5cuXLly5cuXL+UBA58=';

export const NotificationProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const audioRef = useRef(null);
  const socketRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Este navegador no soporta notificaciones');
      return false;
    }

    if (Notification.permission === 'granted') {
      setPermissionGranted(true);
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPermissionGranted(true);
        return true;
      }
    }
    return false;
  }, []);

  // Play notification sound
  const playSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body, icon = '/favicon.ico') => {
    if (permissionGranted && document.hidden) {
      const notification = new Notification(title, {
        body,
        icon,
        badge: icon,
        tag: 'leadgen-notification',
        renotify: true,
        requireInteraction: false
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      setTimeout(() => notification.close(), 5000);
    }
  }, [permissionGranted]);

  // Add notification to list
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);

    // Play sound
    playSound();

    // Show toast
    if (notification.type === 'new_lead') {
      toast.success(notification.title, {
        description: notification.message,
        duration: 5000,
        icon: '🎉'
      });
    } else if (notification.type === 'new_message') {
      toast.info(notification.title, {
        description: notification.message,
        duration: 4000
      });
    } else if (notification.type === 'stage_change') {
      toast.info(notification.title, {
        description: notification.message,
        duration: 3000
      });
    }

    // Show browser notification
    showBrowserNotification(notification.title, notification.message);

    return newNotification;
  }, [playSound, showBrowserNotification]);

  // Notify new lead
  const notifyNewLead = useCallback((leadName, stage = 'Nuevo') => {
    return addNotification({
      type: 'new_lead',
      title: '¡Nuevo lead!',
      message: `${leadName} ha sido agregado al pipeline (${stage})`,
      data: { leadName, stage }
    });
  }, [addNotification]);

  // Notify new message
  const notifyNewMessage = useCallback((contactName, messagePreview) => {
    return addNotification({
      type: 'new_message',
      title: `Mensaje de ${contactName}`,
      message: messagePreview.substring(0, 100),
      data: { contactName, messagePreview }
    });
  }, [addNotification]);

  // Notify stage change
  const notifyStageChange = useCallback((contactName, newStage) => {
    return addNotification({
      type: 'stage_change',
      title: 'Cambio de etapa',
      message: `${contactName} movido a ${newStage}`,
      data: { contactName, newStage }
    });
  }, [addNotification]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Setup Socket.IO connection
  useEffect(() => {
    if (isAuthenticated && token && !socketRef.current) {
      // Request notification permission
      requestPermission();
      
      // Connect to Socket.IO
      const socketUrl = API_URL.replace('/api', '').replace('https://', 'wss://').replace('http://', 'ws://');
      
      socketRef.current = io(API_URL.replace('/api', ''), {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socketRef.current.on('connect', () => {
        console.log('🔌 Socket.IO connected');
        setSocketConnected(true);
      });

      socketRef.current.on('disconnect', () => {
        console.log('🔌 Socket.IO disconnected');
        setSocketConnected(false);
      });

      // Listen for new lead events
      socketRef.current.on('new_lead', (data) => {
        console.log('📥 New lead event:', data);
        notifyNewLead(data.data?.name || 'Nuevo contacto', data.data?.stage || 'Nuevo');
      });

      // Listen for new message events
      socketRef.current.on('new_message', (data) => {
        console.log('📥 New message event:', data);
        notifyNewMessage(
          data.data?.contact_name || 'Contacto',
          data.data?.content || 'Nuevo mensaje'
        );
      });

      // Listen for stage change events
      socketRef.current.on('stage_change', (data) => {
        console.log('📥 Stage change event:', data);
        notifyStageChange(data.contact_name || 'Contacto', data.new_stage || 'Nueva etapa');
      });

      // Listen for lead assignment events
      socketRef.current.on('lead_assigned', (data) => {
        console.log('📥 Lead assigned event:', data);
        addNotification({
          type: 'lead_assigned',
          title: '🎯 Lead asignado',
          message: `${data.contact_name} te ha sido asignado`,
          data: data
        });
      });

      socketRef.current.on('connect_error', (error) => {
        console.log('Socket.IO connection error:', error.message);
      });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, token, requestPermission, notifyNewLead, notifyNewMessage, notifyStageChange]);

  const value = {
    notifications,
    unreadCount,
    permissionGranted,
    socketConnected,
    requestPermission,
    addNotification,
    notifyNewLead,
    notifyNewMessage,
    notifyStageChange,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    playSound
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
